import { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { warmWordsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Loader2, CheckCircle, Send } from 'lucide-react';

export default function WarmWordsPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setLoading(true);
    setError('');
    try {
      await warmWordsApi.create(id, { sender_name: name, message });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-purple-100/50 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-pink-100/50 rounded-full blur-[80px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="border-[#1a1a2e]/5 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#db2777] to-[#7c3aed] flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Heart className="w-7 h-7 text-white" fill="currentColor" />
              </div>
              <h1 className="text-2xl font-bold text-[#1a1a2e]" data-testid="warm-words-heading">
                {sent ? 'Your words have been sent' : 'Send Warm Words'}
              </h1>
              <p className="text-[#1a1a2e]/50 mt-2 text-sm">
                {sent
                  ? 'Thank you for letting them know how they\'ve touched your life.'
                  : 'Someone created a tribute in your honour. Let them know how it made you feel.'}
              </p>
            </div>

            {sent ? (
              <div className="text-center space-y-6">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
                <p className="text-[#1a1a2e]/60 italic text-sm">
                  "Many people go through life never knowing how they have made a positive impact on someone's life."
                </p>
                <Link to={`/tribute/${id}`}>
                  <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full px-8" data-testid="view-tribute-link">
                    View the tribute
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
                )}
                <div>
                  <label className="text-sm text-[#1a1a2e]/70 mb-1 block">Your name</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                    className="border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl py-5"
                    placeholder="Your name" required data-testid="warm-words-name" />
                </div>
                <div>
                  <label className="text-sm text-[#1a1a2e]/70 mb-1 block">Your message</label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                    className="bg-[#FDFBF7] border-[#1a1a2e]/5 focus:border-[#7c3aed] rounded-xl"
                    placeholder="Share how this tribute made you feel, or what this person means to you..."
                    rows={4} required data-testid="warm-words-message" />
                </div>
                <Button type="submit" disabled={loading || !name.trim() || !message.trim()}
                  className="w-full bg-gradient-to-r from-[#db2777] to-[#7c3aed] text-white rounded-full py-6 shadow-lg"
                  data-testid="warm-words-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Warm Words
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[#1a1a2e]/30 text-xs mt-6">&copy; {new Date().getFullYear()} Resilience Community Collective</p>
      </div>
    </div>
  );
}
